import {Component, computed, inject, model, OnInit, signal} from '@angular/core';
import {CompareOptionsComponent} from './components/compare-options/compare-options.component';
import {CompareItemsComponent} from './components/compare-items/compare-items.component';
import {CompareFooterComponent} from './components/compare-footer/compare-footer.component';
import {CompareHeaderComponent} from './components/compare-header/compare-header.component';
import {ComparePrintComponent} from './components/compare-print/compare-print.component';
import {VendorOrderFieldsModalComponent} from './components/vendor-order-fields-modal/vendor-order-fields-modal.component';
import {CompareStore} from './compare.store';
import {OrderStore} from '../../shared/store/order.store';
import {EventService} from '../../shared/services/event.service';
import {ToastService} from '../../shared/services/toast.service';
import {IAddonForItem} from '../../types/IAddonForItem';
import {LoadingService} from '../../shared/services/loading.service';
import {DialogService} from '../../shared/services/dialog.service';
import {weightedPrice} from '../../shared/utils/item.utils';
import {ResultsStore} from '../../shared/store/results.store';
import {PostHogService} from '../../shared/services/posthog.service';
import {IFailedOrder} from '../../shared/services/orders.service';

@Component({
  selector: 'app-compare',
  standalone: true,
  imports: [
    CompareOptionsComponent,
    CompareItemsComponent,
    CompareFooterComponent,
    CompareHeaderComponent,
    ComparePrintComponent,
    VendorOrderFieldsModalComponent,
  ],
  providers: [CompareStore],
  templateUrl: './compare.component.html',
  styleUrl: './compare.component.css',
})
export class CompareComponent implements OnInit {
  public detailsOpen = model<boolean>(true);
  public selectedItemId = signal<number | undefined>(undefined);

  resultsStore = inject(ResultsStore);
  compareStore = inject(CompareStore);
  orderStore = inject(OrderStore);
  private eventService = inject(EventService);
  private toastService = inject(ToastService);
  private dialogService = inject(DialogService);
  private posthogService = inject(PostHogService);
  loading = inject(LoadingService);

  isFlowActive = signal(false);

  constructor() {
    this.loading.watch('Loading', this.resultsStore.isAddonsPending);
    this.loading.watch('Submitting order', this.orderStore.isSubmitPending);
  }

  // Resolved from the store so edits to the compare item (e.g. customer notes) are always current
  selectedItem = computed(() => {
    const id = this.selectedItemId();
    return Object.values(this.resultsStore.compare_items()).find(item => item?.id === id);
  });

  distributorSources = computed(() =>
    (this.selectedItem()?.sourcing ?? []).filter(s => s.type === 'distributor')
  );

  isOrderModalOpen = computed(() =>
    this.isFlowActive() && !!this.orderStore.activeVendor() && !this.orderStore.isSubmitPending()
  );

  ngOnInit() {
    this.compareStore.hydrate();

    const items = Object.values(this.resultsStore.compare_items()).filter(Boolean);
    if (items.length === 1) {
      this.selectedItemId.set(items[0]!.id);
    }

    this.posthogService.posthog.capture('tire_compare_viewed', {
      compare_item_count: items.length,
    });
  }

  onConfirm(mode: 'quote' | 'quote-po' | 'quote-po-submit') {
    // Save only the selected item before continuing. A failed save shouldn't block a quote,
    // but orders are built from the snapshot so it has to succeed before ordering.
    const isOrder = mode === 'quote-po-submit';

    // The order overlay stays up until the first order form opens, startOrderFlow() picks it up
    // before this one is cleared
    this.compareStore.createSnapshot(this.selectedItemId())
      .pipe(this.loading.track(isOrder ? 'Preparing order' : 'Saving'))
      .subscribe({
        next: () => isOrder ? this.startOrderFlow() : this.addToQuote(mode),
        error: (err) => isOrder ? this.toastService.error(err.message) : this.addToQuote(mode),
      });
  }

  addToQuote(mode: 'quote' | 'quote-po' | 'quote-po-submit') {
    const item = this.selectedItem();
    if (item) {
      this.posthogService.posthog.capture('tire_added_to_quote', {
        tire_brand: item.brand,
        tire_model: item.model,
        tire_id: item.id,
      });
      this.eventService.emit('add-item', {item, addon: this.addonForSelectedItem(), mode: mode});
    }
  }

  startOrderFlow() {
    if (!this.distributorSources().length) return this.addToQuote('quote-po-submit');

    this.isFlowActive.set(true);
    this.orderStore.loadVendorsForSources(this.distributorSources())
      .pipe(this.loading.track('Preparing order'))
      .subscribe({
        next: () => this.onVendorFieldsSaved(),
        error: (err) => {
          this.toastService.error(err.message);
          this.endFlow();
        },
      });
  }

  // Called after each vendor form, submits once every vendor's fields have been collected
  onVendorFieldsSaved() {
    if (this.orderStore.activeVendor()) return;

    this.orderStore.submitCart().subscribe({
      next: ({failed}) => failed.length ? this.onOrderFailed(failed) : this.completeFlow(),
      error: (err) => {
        if (this.orderStore.formVendors().length) {
          // Reopen the form for the vendor the API rejected so the user can fix it
          this.orderStore.editVendor(err.errors?.vendor);
          return;
        }
        this.toastService.error(err.message);
        this.endFlow();
      },
    });
  }

  saveForLater() {
    this.compareStore.createSnapshot()
      .pipe(this.loading.track('Preparing order snapshot'))
      .subscribe({
        next: ({token}) => {
          this.posthogService.posthog.capture('tire_saved_for_later', {
            compare_item_count: Object.values(this.resultsStore.compare_items()).filter(Boolean).length,
          });
          this.eventService.emit('save-for-later', {items: this.compareStore.snapshot(), share_link: `${window.location.origin}/select/${token}`});
        },
        error: (err) => this.toastService.error(err.message),
      });
  }

  onOrderCancelled() {
    // Nothing is ordered until every form is filled out, unless this is a retry after a partial failure
    if (!this.orderStore.hasSubmittedOrders()) return this.endFlow();

    this.dialogService.confirm('Are you sure you want to close? Any orders already submitted cannot be cancelled.').then((close) => {
      if (close) this.endFlow();
    });
  }

  private onOrderFailed(failed: IFailedOrder[]) {
    // Let the user fix the failed vendors' fields, submitting the last form retries
    if (this.orderStore.editFailedVendors(failed)) return;

    const message = `${this.orderStore.error()}. Any orders already submitted will not be resubmitted.`;
    this.dialogService.confirm(message, 'Some orders could not be submitted', 'Retry', 'Close').then((retry) => {
      retry ? this.onVendorFieldsSaved() : this.endFlow();
    });
  }

  private endFlow() {
    this.isFlowActive.set(false);
    this.orderStore.reset();
  }

  private completeFlow() {
    const item = this.selectedItem()!;
    this.posthogService.posthog.capture('tire_order_submitted', {
      tire_brand: item.brand,
      tire_model: item.model,
      tire_id: item.id,
    });
    this.eventService.emit('add-item', {item, addon: this.addonForSelectedItem(), mode: "quote-po-submit"});
    this.endFlow();
  }

  private addonForSelectedItem(): IAddonForItem | null {
    const item = this.selectedItem();
    if (!item) return null;
    return this.compareStore.getCompareAddonsForItem()(item.id, weightedPrice(item), item.sourcing.reduce((sum, s) => sum + s.selected_quantity, 0)) ?? null;
  }
}
